import React from 'react';

type SeverityBand = {
  id: string;
  label: string;
  level: string;
  sms: boolean;
  meaning: string;
  categories: string[];
  allowed: boolean;
};

type SeverityMember = {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
};

type SeverityRoster = {
  band_id: string;
  label: string;
  sms: boolean;
  meaning: string;
  members: SeverityMember[];
};

type DailyReport = {
  enabled?: boolean;
  hour_local?: number;
  minute_local?: number;
  emails?: string[];
  phones?: string[];
  last_sent_at?: string;
};

type Delivery = {
  id: number;
  channel: string;
  category: string;
  level: string;
  subject: string;
  status: string;
  created_at: string;
};

type Overview = {
  severity_bands?: SeverityBand[];
  severity_rosters?: SeverityRoster[];
  daily_report?: DailyReport;
  deliveries?: Delivery[];
  note?: string;
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
};

const DEFAULT_BANDS: SeverityBand[] = [
  {
    id: 'critical',
    label: 'Critical',
    level: 'urgent',
    sms: true,
    meaning:
      'P1 — full outage-style traffic collapse or a high tune-up finding that stops work. People here get an immediate email or SMS.',
    categories: ['traffic_anomaly', 'finding_high'],
    allowed: true,
  },
  {
    id: 'high',
    label: 'High',
    level: 'urgent',
    sms: true,
    meaning:
      'P2 — serious but not a total outage. Drafts waiting on a yes that block a campaign. Immediate email or SMS.',
    categories: ['draft_needs_approval'],
    allowed: true,
  },
  {
    id: 'standard',
    label: 'Standard',
    level: 'important',
    sms: false,
    meaning: 'P3 — normal day-to-day traffic. Not page-now — put those people on the Daily report below.',
    categories: ['traffic_digest'],
    allowed: true,
  },
  {
    id: 'low',
    label: 'Low',
    level: 'quiet',
    sms: false,
    meaning:
      'P4 — weekly/monthly recap that can wait. Not page-now — use the Daily report for the digest, or add email here for the recap.',
    categories: ['recap_ready', 'executive_summary'],
    allowed: true,
  },
];

const PRIORITY: Record<string, string> = {
  critical: 'P1',
  high: 'P2',
  standard: 'P3',
  low: 'P4',
};

const formatHourOption = (hour: number) => {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelve).padStart(2, '0')}:00 ${suffix}`;
};

const RazzmatazzNotifyPanel: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
}) => {
  const spanish = language === 'es';
  const [data, setData] = React.useState<Overview | null>(null);
  const [rosters, setRosters] = React.useState<Record<string, SeverityRoster>>({});
  const [error, setError] = React.useState('');
  const [statusNote, setStatusNote] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [busyBand, setBusyBand] = React.useState<string | null>(null);
  const [savingDigest, setSavingDigest] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  /** Collapsed by default — CompliDesk pattern. */
  const [openBands, setOpenBands] = React.useState<Set<string>>(() => new Set());
  const [addingBand, setAddingBand] = React.useState<string | null>(null);
  const [draftEmail, setDraftEmail] = React.useState('');
  const [draftPhone, setDraftPhone] = React.useState('');
  const [phoneUnlocked, setPhoneUnlocked] = React.useState(false);
  const [digestEnabled, setDigestEnabled] = React.useState(false);
  const [digestEmails, setDigestEmails] = React.useState('');
  const [digestPhones, setDigestPhones] = React.useState('');
  const [hourLocal, setHourLocal] = React.useState(8);

  const headers = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const severityBands = data?.severity_bands?.length ? data.severity_bands : DEFAULT_BANDS;
  const busy = loading || savingDigest || testing || busyBand !== null;

  const applyOverview = React.useCallback((payload: Overview) => {
    setData(payload);
    const next: Record<string, SeverityRoster> = {};
    for (const roster of payload.severity_rosters || []) {
      next[roster.band_id] = roster;
    }
    // Fill empty rosters for bands the API omitted.
    for (const band of payload.severity_bands?.length ? payload.severity_bands : DEFAULT_BANDS) {
      if (!next[band.id]) {
        next[band.id] = {
          band_id: band.id,
          label: band.label,
          sms: band.sms,
          meaning: band.meaning,
          members: [],
        };
      }
    }
    setRosters(next);
    const digest = payload.daily_report || {};
    setDigestEnabled(Boolean(digest.enabled));
    setDigestEmails((digest.emails || []).join('\n'));
    setDigestPhones((digest.phones || []).join('\n'));
    setHourLocal(Number.isFinite(Number(digest.hour_local)) ? Number(digest.hour_local) : 8);
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/notify/overview`, {
        headers,
        signal: controller.signal,
      });
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(
          typeof payload.detail === 'string'
            ? payload.detail
            : spanish
              ? `No se pudo cargar (${response.status}).`
              : `Could not load (${response.status}).`,
        );
      }
      applyOverview(payload);
    } catch (err) {
      setData(null);
      setRosters({});
      setError(
        err instanceof Error && err.name === 'AbortError'
          ? (spanish ? 'La carga tardó demasiado.' : 'Load timed out.')
          : err instanceof Error
            ? err.message
            : 'Error',
      );
    } finally {
      window.clearTimeout(timer);
      setLoading(false);
    }
  }, [applyOverview, gatewayBaseUrl, headers, spanish]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const apiJson = async (path: string, init?: RequestInit) => {
    const response = await fetch(`${gatewayBaseUrl}${path}`, {
      ...init,
      headers: { ...headers, ...(init?.headers || {}) },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(typeof payload.detail === 'string' ? payload.detail : `HTTP ${response.status}`);
    }
    return payload;
  };

  const addMember = async (band: SeverityBand) => {
    const email = draftEmail.trim().toLowerCase();
    const phone = draftPhone.trim();
    if (!email && !phone) {
      setError(
        spanish
          ? (band.sms ? 'Pon un correo o un móvil.' : 'Pon un correo.')
          : (band.sms ? 'Enter an email or a mobile number.' : 'Enter an email address.'),
      );
      return;
    }
    setBusyBand(band.id);
    setError('');
    setStatusNote('');
    try {
      const roster = (await apiJson(`/api/v1/razzmatazz/notify/severity/${band.id}/members`, {
        method: 'POST',
        body: JSON.stringify({ email, phone }),
      })) as SeverityRoster;
      setRosters((current) => ({ ...current, [band.id]: roster }));
      setDraftEmail('');
      setDraftPhone('');
      setPhoneUnlocked(false);
      setAddingBand(null);
      setOpenBands((current) => new Set(current).add(band.id));
      setStatusNote(
        spanish
          ? `Añadido a ${band.label}. Guardado en el servidor.`
          : `Added to ${band.label}. Saved to the server.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setBusyBand(null);
    }
  };

  const removeMember = async (bandId: string, contactId: number, label: string) => {
    setBusyBand(bandId);
    setError('');
    setStatusNote('');
    try {
      const roster = (await apiJson(
        `/api/v1/razzmatazz/notify/severity/${bandId}/members/${contactId}`,
        { method: 'DELETE' },
      )) as SeverityRoster;
      setRosters((current) => ({ ...current, [bandId]: roster }));
      setStatusNote(
        spanish
          ? `Quitamos ${label}. Guardado en el servidor.`
          : `Removed ${label}. Saved to the server.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setBusyBand(null);
    }
  };

  const saveDailyReport = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingDigest(true);
    setError('');
    setStatusNote('');
    try {
      const digest = (await apiJson('/api/v1/razzmatazz/notify/daily-report', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: digestEnabled,
          emails: digestEmails,
          phones: digestPhones,
          hour_local: hourLocal,
          minute_local: 0,
        }),
      })) as DailyReport;
      setDigestEnabled(Boolean(digest.enabled));
      setDigestEmails((digest.emails || []).join('\n'));
      setDigestPhones((digest.phones || []).join('\n'));
      setHourLocal(Number(digest.hour_local) || 8);
      // Keep Standard roster in sync (digest members).
      await load();
      setStatusNote(
        spanish
          ? 'Reporte diario guardado en el servidor.'
          : 'Daily report saved to the server.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSavingDigest(false);
    }
  };

  const runNow = async () => {
    setTesting(true);
    setError('');
    try {
      await apiJson('/api/v1/razzmatazz/notify/run', { method: 'POST' });
      await load();
      setStatusNote(spanish ? 'Prueba enviada.' : 'Test run finished.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setTesting(false);
    }
  };

  const lastDelivery = (data?.deliveries || [])[0];
  const lastAlertLine = lastDelivery
    ? (spanish
      ? `Último aviso: ${lastDelivery.subject || lastDelivery.category}`
      : `Last alert sent: ${lastDelivery.subject || lastDelivery.category}`)
    : (spanish ? 'Aún no se ha enviado ningún aviso.' : 'No alerts sent yet.');

  const digestStatus = data?.daily_report?.last_sent_at
    ? (spanish
      ? `Último reporte diario: ${data.daily_report.last_sent_at}.`
      : `Last daily report sent ${data.daily_report.last_sent_at}.`)
    : (digestEnabled
      ? (spanish
        ? 'El reporte diario está activo.'
        : 'Daily report is on. It sends at the time below (Central Time).')
      : (spanish
        ? 'Activa y guarda correos o móviles para el digest.'
        : 'Turn on and save emails or phones to get the Standard digest.'));

  return (
    <div className="razz-notify">
      <div className="razz-traffic-toolbar">
        <div>
          <h3>{spanish ? 'Correo / texto / reporte diario' : 'Email / Text / Daily report'}</h3>
          <p className="razz-muted">
            {spanish
              ? 'Avisos inmediatos (Critical/High) y el digest diario viven aquí. Recaps semanales, mensuales y el resumen ejecutivo están en Reportes.'
              : 'Immediate pages (Critical/High) and the daily digest live here. Weekly/monthly recaps and the executive summary are under Reports.'}
          </p>
        </div>
        <div className="razz-traffic-controls">
          <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void load()}>
            {loading ? (spanish ? 'Cargando…' : 'Loading…') : (spanish ? 'Actualizar' : 'Refresh')}
          </button>
          <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void runNow()}>
            {testing ? (spanish ? 'Enviando…' : 'Sending…') : (spanish ? 'Enviar prueba' : 'Send a test')}
          </button>
        </div>
      </div>

      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {statusNote ? <p className="razz-notify-saved" role="status">{statusNote}</p> : null}

      <section className="razz-settings-block" aria-labelledby="razz-alerts-heading">
        <header className="razz-severity-intro">
          <h4 id="razz-alerts-heading">{spanish ? 'Quién recibe avisos' : 'Who gets alerted'}</h4>
          <p className="razz-muted">
            {spanish
              ? 'Abre Critical o High y usa + para añadir correo y/o móvil para avisos inmediatos. Standard y Low no avisan al momento — pon a esas personas en el reporte diario abajo.'
              : (
                <>
                  Open Critical or High, then use <strong>+</strong>
                  {' '}
                  to add email and/or phone for
                  {' '}
                  <em>immediate</em>
                  {' '}
                  pages. Standard and Low are not page-now — put those people on the Daily report below.
                </>
              )}
          </p>
          <p className="razz-muted razz-settings-status" role="status">{lastAlertLine}</p>
        </header>

        <div className="razz-severity-accordion" aria-label={spanish ? 'Grupos por severidad' : 'Severity groups'}>
          {severityBands.map((band) => {
            const roster = rosters[band.id] || {
              band_id: band.id,
              label: band.label,
              sms: band.sms,
              meaning: band.meaning,
              members: [],
            };
            const members = roster.members || [];
            const open = openBands.has(band.id);
            const isDigestBand = band.id === 'standard';
            const peopleCount = isDigestBand
              ? (digestEnabled
                ? digestEmails.split('\n').filter(Boolean).length + digestPhones.split('\n').filter(Boolean).length
                : 0)
              : members.length;
            const channelLabel = band.sms
              ? (spanish ? 'correo o SMS' : 'email or SMS')
              : (spanish ? 'correo' : 'email');
            const meaning = roster.meaning || band.meaning;

            return (
              <section
                key={band.id}
                className={`razz-severity-acc${peopleCount > 0 ? ' is-on' : ''}`}
              >
                <h5 className="razz-severity-acc-heading">
                  <button
                    type="button"
                    className="razz-severity-acc-trigger"
                    aria-expanded={open}
                    aria-controls={`razz-sev-panel-${band.id}`}
                    id={`razz-sev-btn-${band.id}`}
                    onClick={() => setOpenBands((current) => {
                      const next = new Set(current);
                      if (next.has(band.id)) next.delete(band.id);
                      else next.add(band.id);
                      return next;
                    })}
                  >
                    <span className="razz-severity-acc-caret" aria-hidden="true" />
                    <span className="razz-severity-acc-title">{band.label}</span>
                    <span className="razz-sev-pill">{PRIORITY[band.id] || ''}</span>
                    <span className="razz-sev-pill">
                      {peopleCount}
                      {' '}
                      {peopleCount === 1 ? (spanish ? 'persona' : 'person') : (spanish ? 'personas' : 'people')}
                    </span>
                    <span className="razz-muted razz-sev-channels">{channelLabel}</span>
                  </button>
                </h5>
                <div
                  className="razz-severity-acc-panel"
                  id={`razz-sev-panel-${band.id}`}
                  role="region"
                  aria-labelledby={`razz-sev-btn-${band.id}`}
                  hidden={!open}
                >
                  <p className="razz-muted razz-severity-meaning">{meaning}</p>

                  {isDigestBand ? (
                    <p className="razz-muted razz-severity-empty">
                      {spanish
                        ? 'Los destinatarios del digest se configuran en Reporte diario (abajo).'
                        : 'Digest recipients are configured in Daily report (below).'}
                    </p>
                  ) : (
                    <>
                      <ul className="razz-severity-members">
                        {members.length === 0 ? (
                          <li className="razz-muted razz-severity-empty">
                            {spanish
                              ? 'Nadie en este grupo. Usa + para añadir un contacto.'
                              : 'No one in this group yet. Use + to add a contact.'}
                          </li>
                        ) : (
                          members.map((person) => {
                            const email = person.email || '';
                            const phone = person.phone || '';
                            const label = person.name || email || phone || 'Contact';
                            const meta = email && phone
                              ? `${email} · ${phone}`
                              : email || phone || (band.sms ? 'SMS' : 'email');
                            return (
                              <li key={person.id} className="razz-severity-member">
                                <div>
                                  <strong>{label}</strong>
                                  <span className="razz-muted">
                                    {phone && !email
                                      ? (spanish ? 'contacto SMS' : 'SMS contact')
                                      : meta}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="razz-icon-btn danger"
                                  disabled={busy || !band.allowed}
                                  aria-label={spanish ? `Quitar ${label}` : `Remove ${label}`}
                                  onClick={() => void removeMember(band.id, person.id, label)}
                                >
                                  −
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>

                      <div className="razz-severity-member-toolbar">
                        <button
                          type="button"
                          className="razz-icon-btn add"
                          disabled={busy || !band.allowed}
                          aria-label={spanish ? `Añadir persona a ${band.label}` : `Add person to ${band.label}`}
                          aria-expanded={addingBand === band.id}
                          onClick={() => {
                            setAddingBand((current) => (current === band.id ? null : band.id));
                            setDraftEmail('');
                            setDraftPhone('');
                            setPhoneUnlocked(false);
                          }}
                        >
                          +
                        </button>
                      </div>

                      {addingBand === band.id ? (
                        <form
                          className="razz-add-member-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void addMember(band);
                          }}
                        >
                          <label>
                            {spanish ? 'Correo' : 'Email'}
                            {band.sms ? (spanish ? ' (o déjalo vacío para solo SMS)' : ' (or leave blank for SMS only)') : ''}
                            <input
                              name={`razz-sev-email-${band.id}`}
                              type="email"
                              autoComplete="off"
                              data-1p-ignore="true"
                              data-lpignore="true"
                              placeholder="you@company.com"
                              value={draftEmail}
                              disabled={busy || !band.allowed}
                              onChange={(event) => setDraftEmail(event.target.value)}
                            />
                          </label>
                          {band.sms ? (
                            <label>
                              {spanish ? 'Teléfono (SMS)' : 'Phone (SMS)'}
                              <input
                                name={`razz-sev-phone-${band.id}`}
                                type="tel"
                                inputMode="tel"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                                data-1p-ignore="true"
                                data-lpignore="true"
                                data-form-type="other"
                                placeholder="+12105551234"
                                value={draftPhone}
                                disabled={busy || !band.allowed}
                                readOnly={!phoneUnlocked}
                                onFocus={() => setPhoneUnlocked(true)}
                                onBlur={() => {
                                  if (!draftPhone.trim()) setPhoneUnlocked(false);
                                }}
                                onChange={(event) => setDraftPhone(event.target.value)}
                              />
                            </label>
                          ) : null}
                          <p className="razz-muted razz-add-member-hint">
                            {band.sms
                              ? (spanish
                                ? 'Correo o móvil bastan. Se guarda en el servidor al añadir.'
                                : 'Email or phone is enough. Saved to the server when you add.')
                              : (spanish
                                ? 'Este nivel solo envía correo. Se guarda al añadir.'
                                : 'Email only for this group. Saved when you add.')}
                          </p>
                          <button type="submit" className="dashboard-btn" disabled={busy || !band.allowed}>
                            {spanish ? 'Añadir al grupo' : 'Add to group'}
                          </button>
                        </form>
                      ) : null}
                    </>
                  )}

                  {!band.allowed ? (
                    <p className="razz-muted" role="status">
                      {spanish
                        ? 'Tu rol no puede configurar este nivel.'
                        : 'Your role cannot configure this severity band.'}
                    </p>
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      </section>

      <section className="razz-settings-block" aria-labelledby="razz-daily-heading">
        <header className="razz-severity-intro">
          <h4 id="razz-daily-heading">{spanish ? 'Reporte diario' : 'Daily report'}</h4>
          <p className="razz-muted">
            {spanish
              ? 'Una vez al día: resumen de tráfico (Standard). Aparte de los avisos inmediatos Critical/High.'
              : 'Once a day: traffic digest for Standard events. Separate from Critical/High immediate alerts.'}
          </p>
        </header>

        <form className="razz-daily-report-form" onSubmit={(event) => void saveDailyReport(event)}>
          <div className="razz-daily-schedule" aria-labelledby="razz-daily-toggle-heading">
            <div className="razz-daily-schedule-copy">
              <h5 id="razz-daily-toggle-heading">{spanish ? 'Enviar el reporte diario' : 'Send the daily report'}</h5>
              <p className="razz-muted">
                {spanish
                  ? 'Añade correos y/o móviles de EE. UU. Se guarda en el servidor.'
                  : 'Add emails and/or US phone numbers (text). Saved to the server.'}
              </p>
            </div>
            <label className="razz-daily-toggle" htmlFor="razz-daily-report-enabled">
              <input
                id="razz-daily-report-enabled"
                type="checkbox"
                checked={digestEnabled}
                disabled={busy}
                onChange={(event) => setDigestEnabled(event.target.checked)}
              />
              <span className="razz-daily-toggle-ui" aria-hidden="true" />
              <span className="razz-daily-toggle-text">
                <strong>{digestEnabled ? (spanish ? 'Activado' : 'On') : (spanish ? 'Apagado' : 'Off')}</strong>
                <span className="razz-muted">
                  {spanish ? 'Una vez al día a la hora elegida' : 'Once a day at the time below'}
                </span>
              </span>
            </label>
          </div>

          <div className="razz-daily-report-fields">
            <label className="razz-daily-field">
              <span>{spanish ? 'Correos' : 'Emails'}</span>
              <textarea
                rows={3}
                placeholder={spanish ? 'un correo por línea' : 'one address per line'}
                autoComplete="email"
                value={digestEmails}
                disabled={busy}
                onChange={(event) => setDigestEmails(event.target.value)}
              />
            </label>
            <label className="razz-daily-field">
              <span>{spanish ? 'Mensajes de texto' : 'Text messages'}</span>
              <textarea
                rows={3}
                placeholder={spanish ? 'un teléfono por línea, p. ej. 2488181063' : 'one phone per line, e.g. 2488181063'}
                inputMode="tel"
                value={digestPhones}
                disabled={busy}
                onChange={(event) => setDigestPhones(event.target.value)}
              />
            </label>
          </div>

          <label className="razz-daily-send-at">
            <span>{spanish ? 'Enviar a las (hora de Chicago)' : 'Send at (Central Time)'}</span>
            <select
              value={hourLocal}
              disabled={busy}
              aria-label={spanish ? 'Hora del reporte diario' : 'Daily report hour'}
              onChange={(event) => setHourLocal(Number(event.target.value))}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>{formatHourOption(hour)}</option>
              ))}
            </select>
          </label>

          <div className="razz-notify-actions">
            <button
              type="submit"
              className="dashboard-security-inline-btn active razz-notify-save"
              disabled={savingDigest || loading}
            >
              {savingDigest
                ? (spanish ? 'Guardando…' : 'Saving…')
                : (spanish ? 'Guardar reporte diario' : 'Save daily report')}
            </button>
          </div>
          <p className="razz-muted razz-settings-status" role="status">{digestStatus}</p>
        </form>
      </section>

      <article className="razz-rank razz-notify-deliveries">
        <h3>{spanish ? 'Envíos recientes' : 'Recent sends'}</h3>
        {(data?.deliveries || []).length === 0 ? (
          <p className="razz-muted">
            {spanish
              ? 'Aún no hay envíos. Usa “Enviar prueba” después de añadir contactos.'
              : 'Nothing sent yet. Use “Send a test” after adding contacts.'}
          </p>
        ) : (
          <ul className="razz-traffic-list">
            {(data?.deliveries || []).slice(0, 8).map((row) => (
              <li key={row.id}>
                <strong>{row.created_at?.slice(0, 19)} · {row.status}</strong>
                <span>
                  {row.channel === 'sms' ? (spanish ? 'texto' : 'text') : row.channel}
                  {' · '}
                  {row.subject || row.category}
                </span>
              </li>
            ))}
          </ul>
        )}
      </article>
    </div>
  );
};

export default RazzmatazzNotifyPanel;
